import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, useSidebar } from '@cloudflare/kumo';
import { ArrowUUpLeft, BracketsCurly, PencilSimple, SidebarSimple } from '@phosphor-icons/react';
import {
  Buildings, ChartLineUp, ChatCircleText, Gear, Lock, MagnifyingGlass, Megaphone,
  ShieldCheck, Sparkle, Tray, UsersThree,
} from '@phosphor-icons/react';
import { BRAND_NAME, BRAND_LOGO, BRAND_MARK } from '../lib/brand';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { usePlan, type PlanFeature } from '../contexts/PlanContext';
import { shortcutLabel } from './search/CommandPalette';
import { openSearch } from './search/searchEvents';

/**
 * Menu lateral do app, sobre o `Sidebar` do Kumo.
 *
 * Cada grupo é um bloco recolhível com os seus itens; o estado de cada grupo
 * fica no `localStorage` (`nav_closed_groups`). Com o menu recolhido, o trilho
 * mostra um ícone por grupo (leva ao item ativo ou ao primeiro).
 */

type NavItem = { to: string; label: string; isActive: (path: string, search: string) => boolean; feature?: PlanFeature };
type NavGroup = { key: string; label: string; icon: ReactNode; items: NavItem[]; visible: boolean };

const CLOSED_GROUPS_KEY = 'nav_closed_groups';
const startsWith = (prefix: string) => (path: string) => path === prefix || path.startsWith(prefix + '/');
const PLATFORM_TABS = [
  ['visao-geral', 'Visão geral'],
  ['empresas', 'Empresas'],
  ['usuarios', 'Usuários'],
  ['planos', 'Planos'],
  ['integracoes', 'Integrações'],
  ['backup', 'Backup'],
] as const;

function readClosedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(CLOSED_GROUPS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = useTenant();
  // Visibilidade pelo papel na organização ativa (RBAC), não pelo papel global
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const { has } = usePlan();

  // Permissões como booleanos: `can` é uma função nova a cada render, então
  // o `useMemo` dos grupos depende destes valores primitivos (e não da função).
  const canMembersRead = can('members:read');
  const canBillingRead = can('billing:read');
  const canIntegrations = can('integrations:manage');
  const canMembersManage = can('members:manage');
  const canSettings = canIntegrations;

  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(readClosedGroups);

  const groups: NavGroup[] = useMemo(() => [
    {
      key: 'campanhas', label: 'Campanhas', icon: <Megaphone size={16} />, visible: true,
      items: [
        { to: '/campanhas/interativa', label: 'Campanha interativa', isActive: startsWith('/campanhas/interativa'), feature: 'interactive' as PlanFeature },
        { to: '/sequencias', label: 'Sequências', isActive: startsWith('/sequencias'), feature: 'sequences' as PlanFeature },
      ],
    },
    {
      key: 'ia', label: 'IA', icon: <Sparkle size={16} />, visible: true,
      items: [
        { to: '/dashboard', label: 'Perguntar à IA', isActive: startsWith('/dashboard') },
        { to: '/ia/agentes', label: 'Agentes', isActive: startsWith('/ia/agentes'), feature: 'ai_agents' as PlanFeature },
        { to: '/ia/observabilidade', label: 'Observabilidade', isActive: startsWith('/ia/observabilidade'), feature: 'ai_agents' as PlanFeature },
        { to: '/ia/skills', label: 'Skills', isActive: startsWith('/ia/skills'), feature: 'ai_agents' as PlanFeature },
        { to: '/ia/base', label: 'Base de conhecimento', isActive: startsWith('/ia/base'), feature: 'knowledge_base' as PlanFeature },
        { to: '/ia/ferramentas', label: 'Ferramentas', isActive: startsWith('/ia/ferramentas'), feature: 'ai_agents' as PlanFeature },
        { to: '/ia/widget', label: 'Widget do site', isActive: startsWith('/ia/widget'), feature: 'ai_widget' as PlanFeature },
      ],
    },
    {
      key: 'contatos', label: 'Contatos', icon: <UsersThree size={16} />, visible: true,
      items: [
        { to: '/contatos', label: 'Contatos', isActive: startsWith('/contatos') },
        { to: '/segmentos', label: 'Segmentos', isActive: startsWith('/segmentos') },
        { to: '/campos', label: 'Campos', isActive: startsWith('/campos') },
        { to: '/captacao', label: 'Captação', isActive: startsWith('/captacao') },
        { to: '/automacoes', label: 'Automações', isActive: startsWith('/automacoes') },
        { to: '/lgpd', label: 'LGPD', isActive: startsWith('/lgpd') },
      ],
    },
    {
      key: 'whatsapp', label: 'WhatsApp', icon: <ChatCircleText size={16} />, visible: true,
      items: [
        { to: '/whatsapp', label: 'Conexões', isActive: startsWith('/whatsapp') },
        { to: '/respostas-automaticas', label: 'Respostas automáticas', isActive: startsWith('/respostas-automaticas') },
      ],
    },
    {
      key: 'atendimento', label: 'Atendimento', icon: <Tray size={16} />, visible: true,
      items: [
        { to: '/inbox', label: 'Caixa de entrada', isActive: startsWith('/inbox'), feature: 'inbox' as PlanFeature },
      ],
    },
    {
      key: 'vendas', label: 'Vendas', icon: <ChartLineUp size={16} />, visible: true,
      items: [
        { to: '/funil', label: 'Funil de vendas', isActive: startsWith('/funil') },
        { to: '/metricas', label: 'Métricas', isActive: startsWith('/metricas'), feature: 'advanced_reports' as PlanFeature },
        { to: '/paineis', label: 'Painéis', isActive: startsWith('/paineis') },
      ],
    },
    {
      key: 'organizacao', label: 'Organização', icon: <Buildings size={16} />, visible: true,
      items: [
        { to: '/projetos', label: 'Projetos', isActive: startsWith('/projetos') },
        { to: '/ferramentas', label: 'Ferramentas', isActive: startsWith('/ferramentas') },
        ...(canMembersRead ? [{ to: '/usuarios', label: 'Membros', isActive: startsWith('/usuarios') }] : []),
        ...(canBillingRead ? [{ to: '/faturamento', label: 'Faturamento', isActive: startsWith('/faturamento') }] : []),
        ...(canIntegrations ? [{ to: '/integracoes', label: 'API e webhooks', isActive: startsWith('/integracoes') }] : []),
        ...(canMembersManage ? [{ to: '/auditoria', label: 'Auditoria', isActive: startsWith('/auditoria'), feature: 'audit' as PlanFeature }] : []),
      ],
    },
    {
      key: 'plataforma', label: 'Plataforma', icon: <ShieldCheck size={16} />, visible: isSuperAdmin,
      items: PLATFORM_TABS.map(([slug, label]) => ({
        to: `/super-admin?aba=${slug}`,
        label,
        isActive: (p: string, s: string) =>
          p === '/super-admin' && (new URLSearchParams(s).get('aba') || 'visao-geral') === slug,
      })),
    },
  ].filter((g) => g.visible), [canMembersRead, canBillingRead, canIntegrations, canMembersManage, isSuperAdmin]);

  const { pathname, search } = location;
  const { open, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const rail = !open && !isMobile;

  const toggleGroup = (label: string, isOpen: boolean) => {
    setClosedGroups((current) => {
      const next = { ...current, [label]: !isOpen };
      try { localStorage.setItem(CLOSED_GROUPS_KEY, JSON.stringify(next)); } catch { /* sem storage */ }
      return next;
    });
  };

  const go = (to: string) => {
    navigate(to);
    if (isMobile) setOpenMobile(false);
  };

  const inProfile = startsWith('/perfil')(pathname);

  return (
    <Sidebar role="navigation" aria-label="Navegação principal">
      <Sidebar.Header className="nav-head">
        <a
          href="/dashboard"
          onClick={(e) => { e.preventDefault(); go('/dashboard'); }}
          className="flex items-center gap-2 px-1 py-1 text-kumo-default no-underline"
        >
          {rail
            ? <img src={BRAND_MARK} alt={BRAND_NAME} className="size-6 shrink-0" />
            : <img src={BRAND_LOGO} alt={BRAND_NAME} className="h-6 w-auto shrink-0 [[data-mode=dark]_&]:invert" />}
        </a>
      </Sidebar.Header>
      {inProfile ? <ProfileMenu pathname={pathname} go={go} rail={rail} /> : (
      <Sidebar.Content className="nav-scroll">
        <Sidebar.Group>
          <Sidebar.Menu>
            <Sidebar.MenuButton size="sm"
              className="nav-search"
              icon={<MagnifyingGlass size={16} />}
              tooltip={`Buscar (${shortcutLabel})`}
              onClick={() => openSearch()}
              aria-label={`Buscar (${shortcutLabel})`}
            >
              <span className="flex w-full items-center justify-between">
                <span className="text-kumo-subtle">Buscar…</span>
                <kbd className="text-xs text-kumo-subtle">{shortcutLabel}</kbd>
              </span>
            </Sidebar.MenuButton>
          </Sidebar.Menu>
        </Sidebar.Group>

        {groups.map((group) => {
          const active = group.items.find((item) => item.isActive(pathname, search));
          if (rail) {
            return (
              <Sidebar.Group key={group.key}>
                <Sidebar.Menu>
                  <Sidebar.MenuButton size="sm"
                    icon={group.icon}
                    tooltip={group.label}
                    active={!!active}
                    href={(active ?? group.items[0]).to}
                    onClick={(e) => { e.preventDefault(); go((active ?? group.items[0]).to); }}
                  >
                    {group.label}
                  </Sidebar.MenuButton>
                </Sidebar.Menu>
              </Sidebar.Group>
            );
          }
          return (
            <Sidebar.Group key={group.key}>
              <Sidebar.Collapsible
                open={!closedGroups[group.label]}
                onOpenChange={(isOpen: boolean) => toggleGroup(group.label, isOpen)}
              >
                <Sidebar.CollapsibleTrigger
                  render={
                    <Sidebar.MenuButton size="sm" icon={group.icon} active={!!active && !!closedGroups[group.label]}>
                      {group.label}
                      <Sidebar.MenuChevron />
                    </Sidebar.MenuButton>
                  }
                />
                <Sidebar.CollapsibleContent>
                  <Sidebar.MenuSub>
                    {group.items.map((item) => (
                      <Sidebar.MenuSubButton
                        key={item.to}
                        href={item.to}
                        active={item.isActive(pathname, search)}
                        onClick={(e: React.MouseEvent) => { e.preventDefault(); go(item.to); }}
                      >
                        {item.label}
                        {item.feature && !has(item.feature) && (
                          <Lock size={14} aria-label="Não incluído no seu plano" className="ml-auto shrink-0" />
                        )}
                      </Sidebar.MenuSubButton>
                    ))}
                  </Sidebar.MenuSub>
                </Sidebar.CollapsibleContent>
              </Sidebar.Collapsible>
            </Sidebar.Group>
          );
        })}
        {canSettings && (
          <Sidebar.Group className="nav-account">
          <Sidebar.Menu>
            <Sidebar.MenuButton size="sm"
              icon={<Gear size={16} />}
              tooltip="Configurações"
              active={startsWith('/configuracoes')(pathname)}
              href="/configuracoes"
              onClick={(e) => { e.preventDefault(); go('/configuracoes'); }}
            >
              Configurações
            </Sidebar.MenuButton>
          </Sidebar.Menu>
          </Sidebar.Group>
        )}
      </Sidebar.Content>
      )}
      <Sidebar.Footer className="nav-foot">
        
        {!isMobile && (
          <Sidebar.Menu>
            <Sidebar.MenuButton size="sm"
              icon={<SidebarSimple size={16} />}
              tooltip={open ? 'Recolher menu' : 'Expandir menu'}
              aria-label={open ? 'Recolher menu' : 'Expandir menu'}
              onClick={toggleSidebar}
            />
          </Sidebar.Menu>
        )}
      </Sidebar.Footer>
    </Sidebar>
  );
}

/** Menu do perfil (como o "My Profile" da Cloudflare): troca o menu principal enquanto o usuário está em /perfil */
function ProfileMenu({ pathname, go, rail }: { pathname: string; go: (to: string) => void; rail: boolean }) {
  const access = pathname.startsWith('/perfil/autenticacao') || pathname.startsWith('/perfil/aplicativos') || pathname.startsWith('/perfil/sessoes');
  const [accessOpen, setAccessOpen] = useState(true);
  const link = (to: string, label: string, icon: React.ReactNode, active: boolean) => (
    <Sidebar.MenuButton size="sm" icon={icon} tooltip={label} active={active} href={to} onClick={(e: React.MouseEvent) => { e.preventDefault(); go(to); }}>
      {label}
    </Sidebar.MenuButton>
  );
  return (
    <Sidebar.Content className="nav-scroll">
      <Sidebar.Group>
        <Sidebar.Menu>
          <Sidebar.MenuButton size="sm" icon={<ArrowUUpLeft size={16} />} tooltip="Voltar" href="/dashboard" onClick={(e: React.MouseEvent) => { e.preventDefault(); go('/dashboard'); }}>
            <span className="font-semibold">Meu perfil</span>
          </Sidebar.MenuButton>
        </Sidebar.Menu>
      </Sidebar.Group>
      <Sidebar.Group>
        <Sidebar.Menu>
          {link('/perfil', 'Configurações', <PencilSimple size={16} />, pathname === '/perfil')}
          {rail
            ? link('/perfil/autenticacao', 'Gerenciamento de acesso', <Lock size={16} />, access)
            : (
              <Sidebar.Collapsible open={accessOpen} onOpenChange={setAccessOpen}>
                <Sidebar.CollapsibleTrigger
                  render={<Sidebar.MenuButton size="sm" icon={<Lock size={16} />}>Gerenciamento de acesso<Sidebar.MenuChevron /></Sidebar.MenuButton>}
                />
                <Sidebar.CollapsibleContent>
                  <Sidebar.MenuSub>
                    {([['/perfil/autenticacao', 'Autenticação'], ['/perfil/aplicativos', 'Aplicativos conectados'], ['/perfil/sessoes', 'Sessões ativas']] as const).map(([to, label]) => (
                      <Sidebar.MenuSubButton key={to} href={to} active={pathname.startsWith(to)} onClick={(e: React.MouseEvent) => { e.preventDefault(); go(to); }}>{label}</Sidebar.MenuSubButton>
                    ))}
                  </Sidebar.MenuSub>
                </Sidebar.CollapsibleContent>
              </Sidebar.Collapsible>
            )}
          {link('/perfil/tokens', 'Tokens de API', <BracketsCurly size={16} />, pathname.startsWith('/perfil/tokens'))}
        </Sidebar.Menu>
      </Sidebar.Group>
    </Sidebar.Content>
  );
}
